import { toast, ToastContainer } from "react-toastify";
import { useState, useEffect } from "react";
import { Form, useParams, redirect, useNavigation } from "react-router-dom";
import { API_BASE_URL } from "../../../../config";
import EditProfileFields from "./EditProfileFields";
import ProfileCard from "../profile-card/ProfileCard";
import Loader from "../../../../components/Loader";
import { getAuthToken, getAdminAuthToken } from "../../../../auth/auth";

export default function EditProfilePage() {
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    profile: {
      current_role: "",
      avatar: "",
      bio: "",
      phone: "",
      country: "",
      telegram: "",
      twitter: "",
      facebook: "",
      youtube: "",
      linkedin: "",
      minimum_salary: "",
      minimum_bonus: "",
    },
    skills: [],
    preferences: [],
    certificates: [],
    experiences: [],
    ideal_companies: [],
    other_companies: [],
  });

  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const { id } = useParams();

  useEffect(() => {
    const handleProfile = async () => {
      setIsLoading(true);

      try {
        const response = await fetch(
          `${API_BASE_URL}/professional/${id}/profile`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch profile");
        }

        const result = await response.json();

        setFormData({
          id: id,
          name: result.data.name || "",
          email: result.data.email || "",
          profile: {
            current_role: result.data.profile?.current_role || "",
            avatar: result.data.profile?.avatar || null,
            bio: result.data.profile?.bio || "",
            phone: result.data.profile?.phone || "",
            country: result.data.profile?.country || "",
            telegram: result.data.profile?.telegram || "",
            twitter: result.data.profile?.twitter || "",
            facebook: result.data.profile?.facebook || "",
            youtube: result.data.profile?.youtube || "",
            linkedin: result.data.profile?.linkedin || "",
            availability: result.data.profile?.availability ?? 0,
            minimum_salary: result.data.profile?.minimum_salary || "",
            minimum_bonus: result.data.profile?.minimum_bonus || "",
            verified: result.data.profile?.verified || 0,
          },
          skills: result.data.skills || [],
          preferences: result.data.preferences || [],
          certificates: result.data.certificates || [],
          experiences: result.data.experiences || [],
          ideal_companies: result.data.ideal_companies || [],
          other_companies: result.data.other_companies || [],
        });
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    handleProfile();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name in prev) {
        return { ...prev, [name]: value };
      } else {
        return {
          ...prev,
          profile: {
            ...prev.profile,
            [name]: value,
          },
        };
      }
    });
  };

  const handleFileChange = (file) => {
    setFormData((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        avatar: file,
      },
    }));
  };

  const handleCountryChange = (selectedOption) => {
    setFormData((prevDetails) => ({
      ...prevDetails,
      profile: {
        ...prevDetails.profile,
        country: selectedOption,
      },
    }));
  };

  const handleCertificatesChange = (id, field, value) => {
    const updatedCerificates = formData.certificates.map((curr) =>
      curr.id === id ? { ...curr, [field]: value } : curr
    );

    setFormData((prev) => ({
      ...prev,
      certificates: updatedCerificates,
    }));
  };

  const handleDeleteCertificates = (id) => {
    const updatedCerificates = formData.certificates.filter(
      (curr) => curr.id !== id
    );

    setFormData((prev) => ({
      ...prev,
      certificates: updatedCerificates,
    }));
  };

  const handleAddCertificates = () => {
    const newCertificate = {
      id: new Date().getTime(),
      name: "",
      year_finished: "",
    };

    setFormData((prevState) => ({
      ...prevState,
      certificates: [...prevState.certificates, newCertificate],
    }));
  };

  // experience

  const handleExperienceChange = (id, field, value) => {
    const updatedExperiences = formData.experiences.map((curr) =>
      curr.id === id ? { ...curr, [field]: value } : curr
    );

    setFormData((prevState) => ({
      ...prevState,
      experiences: updatedExperiences,
    }));
  };

  const handleDeleteExperiences = (id) => {
    const updatedExperiences = formData.experiences.filter(
      (curr) => curr.id !== id
    );

    setFormData((prev) => ({
      ...prev,
      experiences: updatedExperiences,
    }));
  };

  const handleAddExperience = () => {
    const newExperience = {
      id: new Date().getTime(),
      company_name: "",
      role: "",
      description: "",
      date_start: "",
      date_end: "",
    };

    setFormData((prevState) => ({
      ...prevState,
      experiences: [...prevState.experiences, newExperience],
    }));
  };

  const handlePreferences = (prefObj) => {
    setFormData((prev) => {
      const alreadySelected = prev.preferences.some((p) => p.id === prefObj.id);

      return {
        ...prev,
        preferences: alreadySelected
          ? prev.preferences.filter((p) => p.id !== prefObj.id)
          : [...prev.preferences, prefObj],
      };
    });
  };

  const handleIdealCompanies = (companyObj) => {
    setFormData((prev) => {
      const companySelected = prev.ideal_companies.some(
        (p) => p.id === companyObj.id
      );

      return {
        ...prev,
        ideal_companies: companySelected
          ? prev.ideal_companies.filter((curr) => curr.id !== companyObj.id)
          : [...prev.ideal_companies, companyObj],
      };
    });
  };

  const handleOtherCompanies = (otherCompany) => {
    setFormData((prev) => {
      const isExisting = prev.other_companies.includes(otherCompany);

      return {
        ...prev,
        other_companies: isExisting
          ? prev.other_companies.filter((curr) => curr !== otherCompany)
          : [...prev.other_companies, otherCompany],
      };
    });
  };

  const handleSkillsChange = (selectedSkillsArray) => {
    setFormData((prev) => ({
      ...prev,
      skills: selectedSkillsArray,
    }));
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="edit-profile-wrapper">
        {isLoading ? (
          <Loader />
        ) : (
          <div className="container">
            <Form method="post" encType="multipart/form-data">
              <div className="row-essy">
                <div className="col-essy-md-12 col-essy-lg-6">
                  <div className="edit-profile-fields">
                    <EditProfileFields
                      details={formData}
                      handleInput={handleChange}
                      handleFileChange={handleFileChange}
                      handleCountryChange={handleCountryChange}
                      handleCertificatesChange={handleCertificatesChange}
                      handleDeleteCertificates={handleDeleteCertificates}
                      handleAddCertificates={handleAddCertificates}
                      handleExperienceChange={handleExperienceChange}
                      handleDeleteExperiences={handleDeleteExperiences}
                      handleAddExperience={handleAddExperience}
                      handlePreferences={handlePreferences}
                      handleIdealCompanies={handleIdealCompanies}
                      handleSkillsChange={handleSkillsChange}
                      handleOtherCompanies={handleOtherCompanies}
                    />
                  </div>
                </div>
                <div className="col-essy-md-12 col-essy-lg-6">
                  <div className="edit-profile-card">
                    <button type="submit" className="btn-all-white">
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                    <ProfileCard details={formData} />
                  </div>
                </div>
              </div>
            </Form>
          </div>
        )}
      </div>
    </>
  );
}

export const editProfileAction = async ({ request, params }) => {
  const formData = await request.formData();

  const token = getAuthToken() || getAdminAuthToken();
  const userId = params.id;

  const data = new FormData();

  data.append("name", formData.get("name"));

  const avatarFile = formData.get("profile_image");

  if (avatarFile instanceof File) {
    data.append("avatar", avatarFile);
  }

  data.append("current_role", formData.get("current_role"));
  data.append("bio", formData.get("bio"));
  data.append("email", formData.get("email"));
  data.append("phone", formData.get("phone"));
  data.append("country", formData.get("country"));
  data.append("linkedin", formData.get("linkedin"));
  data.append("telegram", formData.get("telegram"));
  data.append("facebook", formData.get("facebook"));
  data.append("youtube", formData.get("youtube"));
  data.append("twitter", formData.get("twitter"));

  const skillsRaw = JSON.parse(formData.get("skills"));

  skillsRaw.forEach((id) => {
    data.append("skills[]", id);
  });

  formData.getAll("ideal_companies").forEach((company) => {
    data.append("ideal_companies[]", company);
  });

  formData.getAll("other_companies").forEach((company) => {
    data.append("other_companies[]", company);
  });

  formData.getAll("preferences").forEach((preference) => {
    data.append("preferences[]", preference);
  });

  data.append("minimum_salary", formData.get("minimum_salary"));
  data.append("minimum_bonus", formData.get("minimum_bonus"));

  const certificationsRaw = JSON.parse(formData.get("certifications"));

  const cleanedCertifications = certificationsRaw.map((cert) => ({
    name: cert.name,
    year_finished: cert.year_finished,
  }));
  data.append("certifications", JSON.stringify(cleanedCertifications));

  const experienceRaw = JSON.parse(formData.get("experiences"));

  const cleanedExperience = experienceRaw.map((exp) => ({
    company_name: exp.company_name,
    role: exp.role,
    description: exp.description,
    date_start: exp.date_start,
    date_end: exp.date_end,
  }));

  data.append("experiences", JSON.stringify(cleanedExperience));
  data.append("employee_id", userId);

  console.log("=== FormData sadržaj ===");

  for (let [key, value] of data.entries()) {
    console.log(key, value);
  }

  const response = await fetch(`${API_BASE_URL}/employee/update-profile`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: data,
  });

  const result = await response.json();

  if (result.success === false) {
    return { error: result.message || "Failed to update profile" };
  }

  const isAdmin = window.location.pathname.includes("/admin");

  localStorage.setItem("avatar", result.avatar);

  if (isAdmin) {
    toast.success("Profile updated");
    return null;
  } else {
    return redirect(`/my-profile/${userId}`);
  }
};
